import React from "react";
import axios from "axios";
import { format, startOfWeek, addDays, addWeeks } from "date-fns";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import {
  createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime,
  createEventObjectForTeacherWeekView,
} from "../Calendar/calendarFunctions";
import useDriveMatrix from "./useDriveMatrix";

const SCHOOL_OPTIONS = [
  { id: "mma", label: "Mountainside" },
  { id: "lsma", label: "Lakeside" },
  { id: "tma", label: "Treeside" },
  { id: "mor", label: "Morningside" },
  { id: "psma", label: "Parkside" },
  { id: "den", label: "Denton" },
  { id: "tem", label: "Tempe" },
  { id: "tul", label: "Tulsa" },
];

const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SLOT_MINUTES = 5;
const PIXELS_PER_SLOT = 4; // 5 minutes per slot
const DEFAULT_START_MINUTES = 7 * 60; // 7am
const DEFAULT_END_MINUTES = 21 * 60; // 9pm
const MIN_WINDOW_MINUTES = 14 * 60; // keep a wide window by default

const DragAndDropScheduler2025 = ({ userData }) => {
  const [students, setStudents] = React.useState([]);
  const [teachers, setTeachers] = React.useState([]);
  const [selectedSchool, setSelectedSchool] = React.useState("");
  const [selectedTeacherId, setSelectedTeacherId] = React.useState("");
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [plannedPlacements, setPlannedPlacements] = React.useState([]); // stored as recurring patterns
  const [placedStudentIds, setPlacedStudentIds] = React.useState([]);
  const [draggingStudent, setDraggingStudent] = React.useState(null);
  const [draggingPlacement, setDraggingPlacement] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const dayRefs = React.useRef({});
  const calendarRef = React.useRef(null);
  const [hoverIndicator, setHoverIndicator] = React.useState(null);
  const lastHoverRef = React.useRef({ day: null, label: null });
  const [calendarEvents, setCalendarEvents] = React.useState([]);
  const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState({});
  const [weekViewEvents, setWeekViewEvents] = React.useState([]);
  const [familyInfoCache, setFamilyInfoCache] = React.useState({});
  const [availabilityByDay, setAvailabilityByDay] = React.useState({});
  const [teacherCalendarEvents, setTeacherCalendarEvents] = React.useState([]);
  const [calendarEventsLoaded, setCalendarEventsLoaded] = React.useState(false);
  const [openCalendarModalAfterFetch, setOpenCalendarModalAfterFetch] = React.useState(false);
  const [creatingPlaceDirectories, setCreatingPlaceDirectories] = React.useState(false);
  const [placeDirectory, setPlaceDirectory] = React.useState([]);
  const [driveEdges, setDriveEdges] = React.useState([]);
  const [missingDrivePairs, setMissingDrivePairs] = React.useState([]);
  const [showCalendarEventsModal, setShowCalendarEventsModal] = React.useState(false);
  // Prefer the backend schoolId if available, otherwise use the abbreviation
  const selectedSchoolId = React.useMemo(() => {
    const teacherMatch = teachers.find(
      (t) =>
        t.School?.toLowerCase() === selectedSchool &&
        (t.SchoolID || t.SchoolId || t.schoolId)
    );
    if (teacherMatch) return teacherMatch.SchoolID || teacherMatch.SchoolId || teacherMatch.schoolId;
    const studentMatch = students.find(
      (s) =>
        s.school?.toLowerCase() === selectedSchool &&
        (s.schoolId || s.SchoolId || s.SchoolID)
    );
    if (studentMatch) return studentMatch.schoolId || studentMatch.SchoolId || studentMatch.SchoolID;
    return selectedSchool;
  }, [selectedSchool, students, teachers]);

  const { minutesBetween, minutesMap, seedEdges } = useDriveMatrix(selectedSchoolId, "17:00-18:00");
  const transparentDragImage = React.useMemo(() => {
    const img = new Image();
    img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
    return img;
  }, []);

  const parseTimeToMinutes = (val) => {
    if (val == null) return null;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const parts = val.split(":");
      if (parts.length >= 2) {
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) || 0;
        if (!isNaN(h) && !isNaN(m)) return h * 60 + m;
      }
      const dashParts = val.split("-");
      if (dashParts.length === 2) {
        const start = parseTimeToMinutes(dashParts[0]);
        const end = parseTimeToMinutes(dashParts[1]);
        if (start != null && end != null) return [start, end];
      }
    }
    if (val.start && val.end) {
      const start = parseTimeToMinutes(val.start);
      const end = parseTimeToMinutes(val.end);
      if (start != null && end != null) return [start, end];
    }
    if (val.StartTime && val.EndTime) {
      const start = parseTimeToMinutes(val.StartTime);
      const end = parseTimeToMinutes(val.EndTime);
      if (start != null && end != null) return [start, end];
    }
    if (val.startTime && val.endTime) {
      const start = parseTimeToMinutes(val.startTime);
      const end = parseTimeToMinutes(val.endTime);
      if (start != null && end != null) return [start, end];
    }
    return null;
  };

  const formatAddress = React.useCallback((address) => {
    if (!address) return null;
    const addr = address.Address || address.address || address;
    const parts = [
      addr.addressLine1 || addr.AddressLine1 || addr.address1 || addr.streetAddress || addr.StreetAddress || addr.street,
      addr.addressLine2 || addr.AddressLine2 || addr.address2 || addr.unit,
      [addr.city || addr.City, addr.state || addr.State].filter(Boolean).join(", ").trim(),
      addr.zipcode || addr.Zipcode || addr.zip || addr.Zip || addr.zipCode,
    ].filter(Boolean);
    return parts.filter((p) => p && p !== ",").join(", ") || null;
  }, []);

  const normalizeAvailability = React.useCallback(
    (availabilityDetails) => {
      if (!availabilityDetails) return {};
      const map = {};
      weekDays.forEach((day) => {
        const key = day;
        const lower = day.toLowerCase();
        const shortKey = day.slice(0, 3).toLowerCase();
        const entries =
          availabilityDetails[key] ||
          availabilityDetails[lower] ||
          availabilityDetails[shortKey] ||
          [];
        const blocks = [];
        entries.forEach((entry) => {
          const parsed = parseTimeToMinutes(entry);
          if (Array.isArray(parsed) && parsed.length === 2) {
            blocks.push({ start: parsed[0], end: parsed[1] });
          }
        });
        blocks.sort((a, b) => a.start - b.start);
        map[day] = blocks;
      });
      return map;
    },
    []
  );

  const startOfThisWeek = React.useMemo(
    () => startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 }),
    [weekOffset]
  );
  const displayedWeek = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfThisWeek, i)),
    [startOfThisWeek]
  );

  const driveBucket = "17:00-18:00";
  const makeDriveKey = React.useCallback(
    (fromKey, toKey) => `${selectedSchoolId || selectedSchool || "na"}|${driveBucket}|${fromKey}=>${toKey}`,
    [selectedSchool, selectedSchoolId]
  );

  const timeWindow = React.useMemo(() => {
    const ranges = [];
    weekViewEvents.forEach((event) => {
      const start = event.startDate || event.parsedDate || (event.dateTime ? new Date(event.dateTime) : null);
      if (!start || isNaN(start)) return;
      const duration = Number(event.lessonLength) || (event.rowLength ? event.rowLength * SLOT_MINUTES : 30);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = startMinutes + Math.max(duration, 15);
      ranges.push([startMinutes, endMinutes]);
    });
    plannedPlacements
      .filter((p) => p.teacherId === selectedTeacherId)
      .forEach((placement) => {
        const startMinutes = placement.startMinutes;
        const durationMinutes = Number(placement.lessonLength) || 30;
        ranges.push([startMinutes, startMinutes + durationMinutes]);
      });

    let startMinutes = DEFAULT_START_MINUTES;
    let endMinutes = DEFAULT_END_MINUTES;
    if (ranges.length) {
      const minVal = Math.min(...ranges.map((r) => r[0]));
      const maxVal = Math.max(...ranges.map((r) => r[1]));
      startMinutes = Math.min(minVal, DEFAULT_START_MINUTES);
      endMinutes = Math.max(maxVal, DEFAULT_END_MINUTES);
    }
    if (endMinutes - startMinutes < MIN_WINDOW_MINUTES) {
      endMinutes = startMinutes + MIN_WINDOW_MINUTES;
    }
    const startHour = Math.floor(startMinutes / 60);
    const endHour = Math.ceil(endMinutes / 60);
    const totalMinutes = endMinutes - startMinutes;
    const totalHeightPx = (totalMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
    return { startMinutes, endMinutes, startHour, endHour, totalMinutes, totalHeightPx };
  }, [weekViewEvents, plannedPlacements, selectedTeacherId]);

  React.useEffect(() => {
    if (!selectedTeacherId) {
      setAvailabilityByDay({});
      return;
    }
    const teacher = teachers.find(
      (t) => String(t.TeacherID) === String(selectedTeacherId)
    );
    if (teacher?.AvailabilityDetails) {
      setAvailabilityByDay(normalizeAvailability(teacher.AvailabilityDetails));
    } else {
      setAvailabilityByDay({});
    }
  }, [normalizeAvailability, selectedTeacherId, teachers]);

  const getUnavailableSegments = React.useCallback(
    (dayName) => {
      const avail = availabilityByDay[dayName] || [];
      const segments = [];
      let cursor = timeWindow.startMinutes;
      avail.forEach((block) => {
        const blockStart = block.start;
        const blockEnd = block.end;
        if (blockStart > cursor) {
          segments.push([cursor, Math.min(blockStart, timeWindow.endMinutes)]);
        }
        cursor = Math.max(cursor, blockEnd);
      });
      if (cursor < timeWindow.endMinutes) {
        segments.push([cursor, timeWindow.endMinutes]);
      }
      return segments
        .filter((seg) => seg[1] > seg[0])
        .map((seg) => {
          const top = ((seg[0] - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          const height = ((seg[1] - seg[0]) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          return { top, height };
        });
    },
    [availabilityByDay, timeWindow]
  );

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [studentsRes, teachersRes] = await Promise.all([
          axios.get(`${baseUrl}/engaged-students`, getRequestHeaders()),
          axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders()),
        ]);
        setStudents(studentsRes.data);
        setTeachers(teachersRes.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch place directory for selected school
  React.useEffect(() => {
    if (!selectedSchoolId) return;
    if (String(selectedSchoolId).toLowerCase() === "mor") {
      console.log("Fetching place directory for /place-directory/mor");
    }
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => setPlaceDirectory([]));
  }, [selectedSchoolId]);

  const familiesNeedingNewTeacher = React.useMemo(() => {
    if (!students.length || !teachers.length) return [];
    const leavingTeacherIds = new Set(
      teachers
        .filter(
          (teacher) =>
            teacher.Status === "leaving" ||
            teacher.Status === "Left School" ||
            teacher.Status === "summer-break"
        )
        .map((teacher) => teacher.TeacherID)
    );

    return students.filter((family) => {
      const anyLeavingTeacher = family.students?.some(
        (student) => leavingTeacherIds.has(student.teachersId) && student.studentStatus === "Active"
      );
      const familyFlagged =
        family.familyStatus === "Needs New Teacher" || family.familyStatus === "Summer Break";
      const studentNeedsNewTeacher = family.students?.some(
        (student) => student.studentStatus === "Needs New Teacher"
      );
      return anyLeavingTeacher || familyFlagged || studentNeedsNewTeacher;
    });
  }, [students, teachers]);

  const studentsNeedingReassignment = React.useMemo(() => {
    const list = [];
    familiesNeedingNewTeacher.forEach((family) => {
      if (selectedSchool && family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const uniqueId = `${family.familyId}-${student.studentId}`;
        if (placedStudentIds.includes(uniqueId)) return;
        list.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId: student.studentId,
          studentName: student.studentName,
          instruments: student.instrumentList || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability,
          school: family.school?.toLowerCase(),
        });
      });
    });
    return list;
  }, [familiesNeedingNewTeacher, placedStudentIds, selectedSchool]);

  const instrumentSetForSchool = React.useMemo(() => {
    const set = new Set();
    studentsNeedingReassignment.forEach((student) => {
      student.instruments?.forEach((inst) => inst.parentInstrument && set.add(inst.parentInstrument));
    });
    return set;
  }, [studentsNeedingReassignment]);

  const parseEventDate = React.useCallback((ev) => {
    const candidates = [
      ev.dateTime,
      ev.dateTimeUtc,
      ev.DateTime,
      ev.DateTimeUtc,
      ev.date,
      ev.Date,
      ev.startDate,
      ev.startTime,
      ev.parsedDate,
      ev.start?.dateTime,
      ev.start?.date,
    ].filter(Boolean);
    for (const c of candidates) {
      if (c instanceof Date && !isNaN(c)) return c;
      if (typeof c === "number") {
        const dNum = new Date(c);
        if (dNum && !isNaN(dNum)) return dNum;
      }
      const d = new Date(c);
      if (d && !isNaN(d)) return d;
    }
    return null;
  }, []);

  const getFamilyInfo = React.useCallback(
    async (familyId) => {
      if (!familyId) return null;
      if (familyInfoCache[familyId]) return familyInfoCache[familyId];
      try {
        const res = await axios.get(`${baseUrl}/school-management/${familyId}`, getRequestHeaders());
        const data = res.data;
        setFamilyInfoCache((prev) => ({ ...prev, [familyId]: data }));
        return data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [familyInfoCache]
  );

  const teacherNameById = React.useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => {
      map.set(String(t.TeacherID), t.Name || t.name || "Teacher");
    });
    return map;
  }, [teachers]);

  const teacherOptions = React.useMemo(() => {
    if (!selectedSchool) return [];
    return teachers
      .filter(
        (teacher) =>
          teacher.School?.toLowerCase() === selectedSchool &&
          teacher.WantsMoreStudents &&
          teacher.Status === "Active" &&
          teacher.InstrumentList?.some((inst) => instrumentSetForSchool.has(inst.parentInstrument))
      )
      .map((teacher) => ({
        value: teacher.TeacherID,
        label: teacher.Name,
      }));
  }, [teachers, selectedSchool, instrumentSetForSchool]);

  const teacherIdsForCalendar = React.useMemo(() => {
    const ids = new Set();
    teacherOptions.forEach((t) => {
      if (t.value) ids.add(String(t.value));
    });
    return Array.from(ids);
  }, [teacherOptions]);

  // Fetch next-month calendar events for all teachers in the selected school
  React.useEffect(() => {
    if (!selectedSchoolId || !teacherIdsForCalendar.length) {
      setTeacherCalendarEvents([]);
      setCalendarEventsLoaded(false);
      if (openCalendarModalAfterFetch) {
        setShowCalendarEventsModal(true);
        setOpenCalendarModalAfterFetch(false);
      }
      return;
    }
    setCalendarEventsLoaded(false);
    Promise.all(
      teacherIdsForCalendar.map((tid) =>
        axios
          .get(`${baseUrl}/calendar/teacher/${tid}`, getRequestHeaders())
          .then((res) => (Array.isArray(res.data) ? res.data : []))
          .catch(() => [])
      )
    ).then((all) => {
      const merged = all.flat();
      setTeacherCalendarEvents(merged);
      setCalendarEventsLoaded(true);
      if (openCalendarModalAfterFetch) {
        setShowCalendarEventsModal(true);
        setOpenCalendarModalAfterFetch(false);
      }
    });
  }, [openCalendarModalAfterFetch, parseEventDate, selectedSchoolId, teacherIdsForCalendar]);

  // Refresh place directory when modal opens to ensure badges are current
  React.useEffect(() => {
    if (!showCalendarEventsModal || !selectedSchoolId) return;
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => {});
  }, [selectedSchoolId, setPlaceDirectory, showCalendarEventsModal]);

  // Fetch drive edges for this school on selection change and seed matrix
  React.useEffect(() => {
    if (!selectedSchoolId) return;
    axios
      .get(`${baseUrl}/drive-edges/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        const edges = payload?.edges || (Array.isArray(payload) ? payload : []);
        setDriveEdges(edges);
        seedEdges(edges);
      })
      .catch(() => {
        setDriveEdges([]);
      });
  }, [seedEdges, selectedSchoolId]);

  React.useEffect(() => {
    if (!selectedTeacherId) {
      setCalendarEvents([]);
      setCalendarDaysWithEvents({});
      setWeekViewEvents([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${baseUrl}/calendar/teacher/${selectedTeacherId}`, getRequestHeaders())
      .then((res) => {
        setCalendarEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.log(err);
        setCalendarEvents([]);
      })
      .finally(() => setLoading(false));
  }, [selectedTeacherId]);

  React.useEffect(() => {
    if (!calendarEvents.length) {
      setCalendarDaysWithEvents({});
      setWeekViewEvents([]);
      return;
    }
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
      displayedWeek,
      calendarEvents,
      setCalendarDaysWithEvents
    );
  }, [calendarEvents, displayedWeek]);

  React.useEffect(() => {
    if (!Object.keys(calendarDaysWithEvents).length) {
      setWeekViewEvents([]);
      return;
    }
    createEventObjectForTeacherWeekView(
      displayedWeek,
      calendarDaysWithEvents,
      setWeekViewEvents
    );
  }, [calendarDaysWithEvents, displayedWeek]);

  const positionedEventsByDay = React.useMemo(() => {
    const grouped = {};
    weekDays.forEach((day) => (grouped[day] = []));

    const getLastName = (event) => {
      if (event.familyName) return event.familyName;
      if (event.studentName && event.studentName.includes(" ")) {
        const parts = event.studentName.trim().split(" ");
        return parts[parts.length - 1];
      }
      return event.studentName || "Lesson";
    };

    const buildTooltip = (event, start) => {
      const instrument =
        event.instrument ||
        event.lessonName ||
        event.name ||
        "Instrument N/A";
      const length = event.lessonLength || event.rowLength * SLOT_MINUTES || 30;
      const addr = event.familyAddress || {};
      const addressParts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.zipcode,
      ].filter(Boolean);
      const address = addressParts.join(", ") || "Address N/A";
      const fullName = event.studentName || "Student";
      return `${fullName}\n${instrument}\n${length} mins\n${address}`;
    };

    const mapWeekViewEvent = (event) => {
      const start = event.startDate || event.parsedDate || new Date(event.dateTime);
      if (!start || isNaN(start)) return null;
      const dayName = format(start, "EEEE");
      const minutesFromStart = start.getHours() * 60 + start.getMinutes() - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return null;
      const durationMinutes =
        event.lessonLength ||
        (event.rowLength ? event.rowLength * 5 : null) ||
        30;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = (durationMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = start.getHours() * 60 + start.getMinutes();
      return {
        ...event,
        planned: false,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + durationMinutes,
        label: getLastName(event),
        tooltip: buildTooltip(event, start),
        timeLabel: format(start, "h:mma"),
      };
    };

    weekViewEvents
      .filter((event) => !event.isPendingOffer)
      .forEach((event) => {
        const positioned = mapWeekViewEvent(event);
        if (positioned && grouped[positioned.dayName]) grouped[positioned.dayName].push(positioned);
      });

    plannedPlacements.forEach((placement) => {
      if (placement.teacherId !== selectedTeacherId) return;
      const targetDate = new Date(displayedWeek[placement.dayIndex]);
      targetDate.setHours(Math.floor(placement.startMinutes / 60));
      targetDate.setMinutes(placement.startMinutes % 60);
      targetDate.setSeconds(0);
      const dayName = format(targetDate, "EEEE");
      const minutesFromStart =
        (targetDate.getHours() * 60 + targetDate.getMinutes()) - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = ((placement.lessonLength || 30) / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = targetDate.getHours() * 60 + targetDate.getMinutes();
      grouped[dayName].push({
        ...placement,
        date: targetDate,
        planned: true,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + (placement.lessonLength || 30),
        label: getLastName(placement),
        tooltip: buildTooltip(placement, targetDate),
        timeLabel: format(targetDate, "h:mma"),
      });
    });

    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => a.top - b.top);
    });
    return grouped;
  }, [weekViewEvents, plannedPlacements, selectedTeacherId, timeWindow]);

  const placeKeyLookup = React.useMemo(() => {
    const familyToPlace = new Map();
    const teacherToPlace = new Map();
    const addressToPlace = new Map();
    const labelToPlace = new Map();
    placeDirectory.forEach((entry) => {
      if (!entry.key) return;
      entry.familyIds?.forEach((fid) => familyToPlace.set(String(fid), entry.key));
      entry.teacherIds?.forEach((tid) => teacherToPlace.set(String(tid), entry.key));
      const addrStr = formatAddress(entry.address || entry.label || entry.key);
      if (addrStr) addressToPlace.set(addrStr.toLowerCase(), entry.key);
      if (entry.label) labelToPlace.set(String(entry.label).toLowerCase(), entry.key);
      labelToPlace.set(String(entry.key).toLowerCase(), entry.key);
    });
    driveEdges.forEach((edge) => {
      const originKey = edge.origin?.key || edge.fromKey || edge.origin || edge.from;
      const destKey = edge.destination?.key || edge.toKey || edge.destination || edge.to;
      const originLabel = edge.origin?.label;
      const destLabel = edge.destination?.label;
      if (originKey) {
        labelToPlace.set(String(originKey).toLowerCase(), originKey);
      }
      if (destKey) {
        labelToPlace.set(String(destKey).toLowerCase(), destKey);
      }
      if (originLabel && originKey) {
        labelToPlace.set(String(originLabel).toLowerCase(), originKey);
      }
      if (destLabel && destKey) {
        labelToPlace.set(String(destLabel).toLowerCase(), destKey);
      }
    });
    return { familyToPlace, teacherToPlace, addressToPlace, labelToPlace };
  }, [driveEdges, formatAddress, placeDirectory]);

  const getDriveKey = React.useCallback(
    (event) => {
      const fid = event.familyId || event.familyID || event.FamilyID;
      if (fid && placeKeyLookup.familyToPlace.has(String(fid))) {
        return placeKeyLookup.familyToPlace.get(String(fid));
      }
      const tid = event.teacherId || event.teacherID || event.TeacherID;
      if (tid && placeKeyLookup.teacherToPlace.has(String(tid))) {
        return placeKeyLookup.teacherToPlace.get(String(tid));
      }
      const addr = formatAddress(event.familyAddress || event.address);
      if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
        return placeKeyLookup.addressToPlace.get(addr.toLowerCase());
      }
      const label = (event.familyName || event.studentName || event.label || addr || "").toLowerCase();
      if (label && placeKeyLookup.labelToPlace.has(label)) {
        return placeKeyLookup.labelToPlace.get(label);
      }
      return (
        fid ||
        tid ||
        event.studentId ||
        event.familyName ||
        event.studentName ||
        event.label
      );
    },
    [formatAddress, placeKeyLookup]
  );

  const driveSegmentsByDay = React.useMemo(() => {
    const res = {};
    weekDays.forEach((d) => (res[d] = []));
    weekDays.forEach((day) => {
      const events = (positionedEventsByDay[day] || []).slice().sort((a, b) => a.startMinutesOfDay - b.startMinutesOfDay);
      for (let i = 0; i < events.length - 1; i++) {
        const current = events[i];
        const next = events[i + 1];
        // Skip drive time when consecutive events belong to the same family
        if (
          current.familyId &&
          next.familyId &&
          String(current.familyId) === String(next.familyId)
        ) {
          continue;
        }
        const minutes = minutesBetween(getDriveKey(current), getDriveKey(next));
        const top = (current.top + current.height + next.top) / 2;
        res[day].push({ minutes, top });
      }
    });
    return res;
  }, [getDriveKey, minutesBetween, positionedEventsByDay]);

  // Batch scan drive edges for all known endpoints when school changes or data set changes
  React.useEffect(() => {
    if (!selectedSchool) return;
    // map ids -> placeKey (only place keys are valid)
    const familyToPlace = new Map();
    const teacherToPlace = new Map();
    placeDirectory.forEach((entry) => {
      if (Array.isArray(entry.familyIds)) {
        entry.familyIds.forEach((fid) => familyToPlace.set(String(fid), entry.key));
      }
      if (Array.isArray(entry.teacherIds)) {
        entry.teacherIds.forEach((tid) => teacherToPlace.set(String(tid), entry.key));
      }
    });

    // Gather all location keys: reassignment families, teacher homes, teacher event families (next month), current placements, current week events
    const locationKeys = new Set();
    const fallbackLocationKeys = new Set(); // use familyId as fallback only for counting
    studentsNeedingReassignment.forEach((s) => {
      const key = familyToPlace.get(String(s.familyId));
      if (key) locationKeys.add(key);
      fallbackLocationKeys.add(String(s.familyId));
    });
    teacherCalendarEvents.forEach((ev) => {
      const key = familyToPlace.get(String(ev.familyId));
      if (key) locationKeys.add(key);
      fallbackLocationKeys.add(String(ev.familyId));
    });
    weekViewEvents.forEach((ev) => {
      const key = familyToPlace.get(String(ev.familyId));
      if (key) locationKeys.add(key);
      fallbackLocationKeys.add(String(ev.familyId));
    });
    plannedPlacements.forEach((p) => {
      const key = familyToPlace.get(String(p.familyId));
      if (key) locationKeys.add(key);
      fallbackLocationKeys.add(String(p.familyId));
    });
    placeDirectory.forEach((entry) => {
      if (Array.isArray(entry.teacherIds) && entry.teacherIds.length > 0) {
        locationKeys.add(entry.key);
        entry.teacherIds.forEach((tid) => fallbackLocationKeys.add(String(tid)));
      }
    });
    teacherOptions.forEach((t) => {
      const key = teacherToPlace.get(String(t.value));
      if (key) locationKeys.add(key);
      fallbackLocationKeys.add(String(t.value));
    });

    const primaryKeys = locationKeys.size > 1 ? Array.from(locationKeys) : [];
    const fallbackKeys =
      primaryKeys.length < 2 && fallbackLocationKeys.size > 1
        ? Array.from(fallbackLocationKeys)
        : [];
    const keysArr = primaryKeys.length > 1 ? primaryKeys : fallbackKeys;

    const pairs = [];
    for (let i = 0; i < keysArr.length; i++) {
      for (let j = 0; j < keysArr.length; j++) {
        if (i === j) continue;
        const fromKey = keysArr[i];
        const toKey = keysArr[j];
        pairs.push({ fromKey, toKey });
      }
    }

    const missing =
      primaryKeys.length > 1
        ? pairs.filter((p) => !minutesMap.has(makeDriveKey(p.fromKey, p.toKey)))
        : pairs;
    setMissingDrivePairs(missing);
  }, [
    makeDriveKey,
    minutesBetween,
    placeDirectory,
    plannedPlacements,
    selectedSchool,
    studentsNeedingReassignment,
    teacherCalendarEvents,
    teacherOptions,
    minutesMap,
    selectedSchoolId,
    weekViewEvents,
  ]);

  const dropdownTeachersForModal = React.useMemo(() => {
    const directoryTeacherIds = new Set();
    const directoryAddresses = new Set();
    placeDirectory.forEach((entry) => {
      entry.teacherIds?.forEach((tid) => directoryTeacherIds.add(String(tid)));
      if (entry.address) directoryAddresses.add(String(entry.address).toLowerCase());
      if (entry.label) directoryAddresses.add(String(entry.label).toLowerCase());
      if (entry.key) directoryAddresses.add(String(entry.key).toLowerCase());
    });

    const optionIds = new Set(teacherOptions.map((t) => String(t.value)));
    const list = [];
    teachers
      .filter(
        (t) =>
          optionIds.has(String(t.TeacherID)) &&
          t.School?.toLowerCase() === selectedSchool
      )
      .forEach((t) => {
        const id = String(t.TeacherID);
        const address = formatAddress(
          t.Address ||
            t.address ||
            t.HomeAddress || {
              addressLine1: t.Address1,
              addressLine2: t.Address2,
              city: t.City,
              state: t.State,
              zipcode: t.Zip,
            }
        );
        list.push({
          id: `teacher-${id}`,
          teacherId: id,
          teacherName: t.Name || "Teacher",
          address,
          hasDirectory:
            directoryTeacherIds.has(id) ||
            (address && directoryAddresses.has(address.toLowerCase())),
        });
      });
    return list;
  }, [formatAddress, placeDirectory, selectedSchool, teacherOptions, teachers]);

  const placeDirectoriesForModal = React.useMemo(() => {
    if (!placeDirectory.length) return [];
    return placeDirectory.map((entry, idx) => ({
      id: entry.key || entry.label || entry.address || `pd-${idx}`,
      label: entry.label || entry.key || entry.address || "Place",
      address: entry.address || entry.label || entry.key || "",
      roles: Array.isArray(entry.roles) && entry.roles.length ? entry.roles.join(", ") : "location",
    }));
  }, [placeDirectory]);

  const placeDirectoryPairsCount = React.useMemo(() => {
    const n = placeDirectory.length || 0;
    return (n * (n - 1)) / 2;
  }, [placeDirectory]);

  const driveEdgesForModal = React.useMemo(() => {
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      const label = pd.label || pd.key || pd.address || "Place";
      if (pd.key) labelByKey.set(pd.key, label);
    });
    const edges = (driveEdges || []).map((edge, idx) => {
      const fromKey =
        edge.origin?.key || edge.fromKey || edge.origin || edge.from || edge.key || `edge-${idx}-from`;
      const toKey =
        edge.destination?.key || edge.toKey || edge.destination || edge.to || `edge-${idx}-to`;
      return {
        id: edge.id || edge._id || `${fromKey}=>${toKey}-${idx}`,
        fromKey,
        toKey,
        fromLabel: edge.origin?.label || labelByKey.get(fromKey) || fromKey,
        toLabel: edge.destination?.label || labelByKey.get(toKey) || toKey,
        minutes: edge.minutes,
        bucket: edge.todBucket || edge.bucket,
      };
    });
    edges.sort((a, b) => (a.fromLabel || "").localeCompare(b.fromLabel || ""));
    return edges;
  }, [driveEdges, placeDirectory]);

  const createPlaceDirectoriesFromModal = React.useCallback(async () => {
    if (!dropdownTeachersForModal.length || !selectedSchoolId) return;
    setCreatingPlaceDirectories(true);
    try {
      const directories = dropdownTeachersForModal
        .filter((f) => f.address)
        .map((f) => ({
          key: f.address || f.teacherName,
          address: f.address,
          label: f.teacherName,
          roles: ["teacher"],
          familyIds: [],
          teacherIds: f.teacherId ? [f.teacherId] : [],
        }));

      if (!directories.length) {
        setCreatingPlaceDirectories(false);
        return;
      }

      await axios
        .post(
          `${baseUrl}/place-directories/bulk`,
          {
            schoolId: selectedSchoolId,
            directories,
          },
          getRequestHeaders()
        )
        .catch((err) => {
          console.log("place-directories bulk failed", err?.response?.data || err?.message);
        });

      // refresh place directory
      axios
        .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
        .then((res) => setPlaceDirectory(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    } finally {
      setCreatingPlaceDirectories(false);
    }
  }, [dropdownTeachersForModal, selectedSchoolId, setPlaceDirectory]);

  const onDragStart = (student, domEvent) => {
    if (domEvent?.dataTransfer && transparentDragImage) {
      domEvent.dataTransfer.setDragImage(transparentDragImage, 0, 0);
    }
    setDraggingStudent(student);
    setDraggingPlacement(null);
  };

  const onDragStartPlacement = (placement) => {
    if (!placement.planned) return;
    setDraggingPlacement(placement);
    setDraggingStudent(null);
  };

  const onDragOverDayArea = (dayName, e) => {
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    const slotDate = new Date(startOfThisWeek);
    slotDate.setDate(slotDate.getDate() + weekDays.indexOf(dayName));
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    const label = format(slotDate, "h:mma");
    if (lastHoverRef.current.day !== dayName || lastHoverRef.current.label !== label) {
      lastHoverRef.current = { day: dayName, label };
      setHoverIndicator({ day: dayName, label });
    }
    e.preventDefault();
  };

  const onDropOnDayArea = async (dayName, e) => {
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    const dayIndex = weekDays.indexOf(dayName);
    const slotDate = new Date(displayedWeek[dayIndex]);
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    // hydrate placement details
    if (draggingStudent) {
      let instrumentLabel =
        draggingStudent.instruments?.[0]?.parentInstrument ||
        draggingStudent.instruments?.[0]?.instrument ||
        null;
      let lessonLength = draggingStudent.lessonLength || 30;
      let familyAddress = draggingStudent.familyAddress || null;

      const familyInfo = await getFamilyInfo(draggingStudent.familyId);
      if (familyInfo) {
        const match =
          familyInfo.students?.find(
            (s) => String(s.studentId) === String(draggingStudent.studentId)
          ) || {};
        if (!instrumentLabel && match.instrumentList?.length) {
          instrumentLabel = match.instrumentList[0]?.parentInstrument || null;
        }
        if (!lessonLength && match.lessonLength) {
          lessonLength = match.lessonLength;
        }
        familyAddress = familyInfo.address || familyInfo.familyAddress || familyAddress;
      }

      const placement = {
        id: `${draggingStudent.id}-${dayName}-${slotMinutes}`,
        placementKey: draggingStudent.id,
        familyId: draggingStudent.familyId,
        studentName: draggingStudent.studentName,
        familyName: draggingStudent.familyName,
        dayIndex,
        teacherId: selectedTeacherId,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
        instrument: instrumentLabel,
        familyAddress,
      };
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => !(p.id === placement.id));
        return [...filtered, placement];
      });
      setPlacedStudentIds((prev) => (prev.includes(draggingStudent.id) ? prev : [...prev, draggingStudent.id]));
      setDraggingStudent(null);
    } else if (draggingPlacement) {
      const placementKey = draggingPlacement.placementKey || draggingPlacement.id;
      const lessonLength = draggingPlacement.lessonLength || 30;
      const updatedPlacement = {
        ...draggingPlacement,
        id: `${placementKey}-${dayName}-${slotMinutes}`,
        placementKey,
        dayIndex,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
      };
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => (p.placementKey || p.id) !== placementKey);
        return [...filtered, updatedPlacement];
      });
      setDraggingPlacement(null);
    }
    lastHoverRef.current = { day: null, label: null };
    setHoverIndicator(null);
  };

  const renderStudentCard = (student) => (
    <div
      key={student.id}
      className="dds2025-student-card"
      style={{
        background: "#f1f5f9",
        border: "1px solid #e2e8f0",
        padding: "14px",
        borderRadius: "10px",
      }}
      draggable
      onDragStart={(e) => onDragStart(student, e)}
    >
      <div className="dds2025-student-header">
        <div className="dds2025-student-name-row">
          <span className="dds2025-drag-hint">⋮⋮</span>
          <div className="dds2025-student-name">
            {student.studentName} {student.familyName}
          </div>
        </div>
        <div className="dds2025-student-chips">
          <span className="dds2025-chip">{student.lessonLength || "--"} mins</span>
          {student.instruments?.length ? (
            student.instruments.map((inst) => (
              <span key={inst.id || inst.parentInstrument} className="dds2025-chip dds2025-chip--neutral">
                {inst.parentInstrument}
              </span>
            ))
          ) : (
            <span className="dds2025-chip dds2025-chip--neutral">Instrument TBD</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="dds2025">
      <div className="dds2025__filters">
        <div className="dds2025__filter">
          <label>School</label>
          <select
            value={selectedSchool}
            onChange={(e) => {
              setSelectedSchool(e.target.value);
              setSelectedTeacherId("");
              setShowCalendarEventsModal(false);
              setOpenCalendarModalAfterFetch(true);
            }}
          >
            <option value="">Select a school</option>
            {SCHOOL_OPTIONS.map((school) => (
              <option key={school.id} value={school.id}>
                {school.label}
              </option>
            ))}
          </select>
        </div>
        <div className="dds2025__filter">
          <label>Teacher (instrument match)</label>
          <select
            value={selectedTeacherId}
            onChange={(e) => setSelectedTeacherId(e.target.value)}
            disabled={!teacherOptions.length}
          >
            <option value="">Select a teacher</option>
            {teacherOptions.map((teacher) => (
              <option key={teacher.value} value={teacher.value}>
                {teacher.label}
              </option>
            ))}
          </select>
        </div>
        {loading && <div className="dds2025__status">Loading data…</div>}
      </div>

      <div className="dds2025__grid">
        <div className="dds2025__students">
          <div className="dds2025__column-title">
            Students needing reassignment ({studentsNeedingReassignment.length})
          </div>
          <div className="dds2025__student-list">
            {studentsNeedingReassignment.map((student) => renderStudentCard(student))}
            {!studentsNeedingReassignment.length && (
              <div className="dds2025__empty">No students to reassign for this school.</div>
            )}
          </div>
        </div>

        <div className="dds2025__calendar">
          <div className="dds2025__column-title">
            <div className="dds2025__calendar-header">
              <div className="dds2025__calendar-week">
                {format(displayedWeek[0], "MMM d")} – {format(displayedWeek[6], "MMM d, yyyy")}
              </div>
              <div className="dds2025__calendar-nav">
                <button className="dds2025__nav-btn" onClick={() => setWeekOffset((prev) => prev - 1)}>&lt;</button>
                <button className="dds2025__nav-btn" onClick={() => setWeekOffset((prev) => prev + 1)}>&gt;</button>
              </div>
            </div>
            <div className="dds2025__drive-cta">
              <span className="dds2025__drive-text">
                Drive pairs missing: {missingDrivePairs.length}
              </span>
            </div>
            {!selectedTeacherId && <div>Choose a teacher to view their week</div>}
          </div>
          <div className="dds2025__week-grid" ref={calendarRef}>
            <div className="dds2025__time-col" style={{ height: timeWindow.totalHeightPx }}>
              {Array.from({ length: timeWindow.endHour - timeWindow.startHour + 1 }).map((_, i) => {
                const hour = timeWindow.startHour + i;
                return (
                  <div key={hour} className="dds2025__time-marker" style={{ top: (((hour * 60) - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT }}>
                    {format(new Date().setHours(hour, 0, 0, 0), "h a")}
                  </div>
                );
              })}
            </div>
            {weekDays.map((day) => (
              <div key={day} className="dds2025__day-grid">
                <div className="dds2025__day-header">{day}</div>
                <div
                  className="dds2025__day-body"
                  ref={(el) => (dayRefs.current[day] = el)}
                  style={{ height: timeWindow.totalHeightPx }}
                  onDragOver={(e) => onDragOverDayArea(day, e)}
                  onDrop={(e) => onDropOnDayArea(day, e)}
                  onDragLeave={() => {
                    lastHoverRef.current = { day: null, label: null };
                    setHoverIndicator(null);
                  }}
                >
                  {(driveSegmentsByDay[day] || []).map((seg, i) => (
                    <div
                      key={`drive-${day}-${i}`}
                      className="dds2025__drive-chip"
                      style={{ top: seg.top }}
                      title={Number.isFinite(seg.minutes) ? `Drive ${seg.minutes}m` : "Drive time unavailable"}
                    >
                      Drive {Number.isFinite(seg.minutes) ? `${seg.minutes}m` : "--"}
                    </div>
                  ))}
                  {getUnavailableSegments(day).map((seg, idx) => (
                    <div
                      key={`unavail-${day}-${idx}`}
                      className="dds2025__unavailable"
                      style={{ top: seg.top, height: seg.height }}
                    />
                  ))}
                  {hoverIndicator?.day === day && (
                    <div className="dds2025__hover-badge">{hoverIndicator.label}</div>
                  )}
                  <div className="dds2025__grid-lines">
                    {Array.from({ length: Math.ceil(timeWindow.totalMinutes / SLOT_MINUTES) }).map((_, idx) => {
                      const minute = idx * SLOT_MINUTES;
                      const top = (minute / SLOT_MINUTES) * PIXELS_PER_SLOT;
                      const isHalfHour = minute % 30 === 0;
                      const isHour = minute % 60 === 0;
                      return (
                        <div
                          key={idx}
                          className={`dds2025__grid-line ${isHour ? "dds2025__grid-line--hour" : isHalfHour ? "dds2025__grid-line--half" : ""}`}
                          style={{ top }}
                        />
                      );
                    })}
                  </div>
                  {(positionedEventsByDay[day] || []).map((event) => {
                    const eventKey = event.placementKey || event.id;
                    const isDraggingThis =
                      draggingPlacement && (draggingPlacement.placementKey || draggingPlacement.id) === eventKey;
                    return (
                      <div
                        key={event.id || `${event.studentId}-${event.timeLabel}`}
                        className={`dds2025__event-block ${event.planned ? "dds2025__event-block--planned" : ""}`}
                        style={{
                          top: event.top,
                          height: event.height,
                        }}
                        data-tooltip={isDraggingThis ? "" : event.tooltip}
                        draggable={event.planned}
                        onDragStart={() => onDragStartPlacement(event)}
                        onDragEnd={() => setDraggingPlacement(null)}
                      >
                        <div className="dds2025__event-row">
                          <div className="dds2025__event-time">{event.timeLabel}</div>
                          <div className="dds2025__event-name">{event.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .dds2025 { display: flex; flex-direction: column; gap: 16px; }
        .dds2025__filters { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
        .dds2025__filter { display: flex; flex-direction: column; font-size: 14px; }
        .dds2025__filter select { padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; }
        .dds2025__status { font-size: 12px; color: #6b7280; }
        .dds2025__grid { display: grid; grid-template-columns: 320px 1fr; gap: 16px; }
        .dds2025__column-title { font-weight: 700; margin-bottom: 8px; }
        .dds2025__students { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08); }
        .dds2025__student-list { display: flex; flex-direction: column; gap: 14px; max-height: 70vh; overflow: auto; padding: 6px 2px; }
        .dds2025__student-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #f1f5f9; cursor: grab; box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06); transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease; }
        .dds2025__student-card:active { cursor: grabbing; }
        .dds2025__student-card:hover { transform: translateY(-2px); border-color: #cbd5e1; box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12); }
        .dds2025-student-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
        .dds2025-student-name-row { display: grid; grid-template-columns: 24px 1fr; align-items: center; gap: 6px; }
        .dds2025-drag-hint { font-size: 14px; color: #94a3b8; }
        .dds2025-chip { background: #eef2ff; color: #4338ca; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
        .dds2025-chip--neutral { background: #f1f5f9; color: #475569; }
        .dds2025__student-name { font-weight: 800; font-size: 13px; }
        .dds2025__student-family { font-size: 10px; color: #6b7280; }
        .dds2025-student-name { font-weight: 800; font-size: 13px; }
        .dds2025-student-chips { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
        .dds2025__calendar { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; background: #fff; overflow: auto; }
        .dds2025__calendar-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .dds2025__calendar-week { font-weight: 700; }
        .dds2025__calendar-nav { display: flex; gap: 6px; }
        .dds2025__nav-btn { border: 1px solid #e2e8f0; background: #fff; padding: 4px 8px; border-radius: 6px; cursor: pointer; }
        .dds2025__drive-cta { display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 12px; color: #475569; }
        .dds2025__drive-text { font-size: 12px; }
        .dds2025__week-grid { display: grid; grid-template-columns: 64px repeat(7, minmax(160px, 1fr)); gap: 10px; align-items: start; }
        .dds2025__time-col { position: relative; border-right: 1px solid #e2e8f0; }
        .dds2025__time-marker { position: absolute; left: 4px; font-size: 11px; color: #94a3b8; transform: translateY(14px); }
        .dds2025__day-grid { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; background: #f8fafc; }
        .dds2025__day-header { background: #eef2ff; padding: 6px 10px; font-weight: 700; position: sticky; top: 0; z-index: 2; }
        .dds2025__day-body { position: relative; overflow-y: auto; background: #fff; }
        .dds2025__grid-lines { position: absolute; inset: 0; }
        .dds2025__grid-line { position: absolute; left: 0; right: 0; height: 1px; background: #f1f5f9; }
        .dds2025__grid-line--half { background: #e2e8f0; }
        .dds2025__grid-line--hour { background: #cbd5e1; }
        .dds2025__unavailable { position: absolute; left: 0; right: 0; background: rgba(148, 163, 184, 0.25); pointer-events: none; }
        .dds2025__drive-chip { position: absolute; left: 50%; transform: translate(-50%, -50%); background: #ecfdf3; color: #166534; border: 1px solid #86efac; border-radius: 999px; padding: 2px 8px; font-size: 10px; box-shadow: 0 2px 6px rgba(15,23,42,0.12); pointer-events: none; white-space: nowrap; z-index: 4; }
        .dds2025__event-block { position: absolute; left: 6px; right: 6px; padding: 4px 6px; border-radius: 6px; border: 1px solid #e2e8f0; background: #e0f2fe; font-size: 12px; box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06); box-sizing: border-box; }
        .dds2025__event-block--planned { background: #dcfce7; border-color: #a7f3d0; }
        .dds2025__event-row { display: flex; align-items: center; gap: 6px; }
        .dds2025__event-time { font-weight: 700; font-size: 11px; white-space: nowrap; }
        .dds2025__event-name { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dds2025__event-block::after { content: attr(data-tooltip); position: absolute; left: 0; top: -4px; transform: translateY(-100%); background: #0f172a; color: #fff; padding: 6px 8px; border-radius: 8px; box-shadow: 0 8px 16px rgba(15,23,42,0.24); font-size: 11px; white-space: pre-line; opacity: 0; pointer-events: none; transition: opacity 80ms ease, transform 80ms ease; z-index: 5; }
        .dds2025__event-block:hover::after { opacity: 1; transform: translateY(-110%); }
        .dds2025__hover-badge { position: absolute; top: 8px; right: 8px; padding: 4px 8px; font-size: 11px; font-weight: 700; color: #0f172a; background: rgba(255,255,255,0.95); border: 1px solid #cbd5e1; border-radius: 8px; z-index: 3; box-shadow: 0 4px 10px rgba(15,23,42,0.08); pointer-events: none; }
        .dds2025__empty { font-size: 12px; color: #9ca3af; padding: 12px 0; }
        .dds2025__modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.35); display: flex; align-items: center; justify-content: center; z-index: 3000; }
        .dds2025__modal { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; max-width: 360px; width: 90%; box-shadow: 0 10px 30px rgba(15,23,42,0.25); }
        .dds2025__modal--wide { max-width: 640px; }
        .dds2025__modal-title { font-weight: 700; margin-bottom: 8px; }
        .dds2025__modal-body { font-size: 14px; color: #475569; margin-bottom: 12px; }
        .dds2025__modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .dds2025__section { margin-bottom: 14px; }
        .dds2025__section-title { font-weight: 700; margin-bottom: 6px; }
        .dds2025__simple-list { display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow: auto; }
        .dds2025__simple-row { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; background: #f8fafc; }
        .dds2025__simple-name { font-weight: 700; color: #0f172a; }
        .dds2025__simple-sub { font-size: 12px; color: #475569; }
        .dds2025__missing-list { display: flex; flex-direction: column; gap: 8px; max-height: 440px; overflow: auto; }
        .dds2025__missing-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 8px; padding: 8px 10px; }
        .dds2025__missing-name { font-weight: 700; color: #0f172a; }
        .dds2025__missing-address { font-size: 12px; color: #475569; }
        .dds2025__tag { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 2px; font-size: 11px; color: #334155; }
        .dds2025__badge { display: inline-flex; align-items: center; padding: 2px 6px; margin-left: 8px; border-radius: 999px; background: #ecfdf3; color: #166534; font-size: 10px; font-weight: 700; border: 1px solid #86efac; }
      `}</style>
      {showCalendarEventsModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">
              Dropdown Teachers — {dropdownTeachersForModal.length}
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__modal-actions" style={{ justifyContent: "flex-start", marginBottom: 8 }}>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Drive Edges ({driveEdgesForModal.length})</div>
                <div className="dds2025__simple-list">
                  {driveEdgesForModal.length ? (
                    driveEdgesForModal.map((edge) => (
                      <div key={edge.id} className="dds2025__simple-row">
                        <div className="dds2025__simple-name">
                          {edge.fromLabel} → {edge.toLabel}
                        </div>
                        <div className="dds2025__simple-sub">
                          {Number.isFinite(edge.minutes) ? `${edge.minutes} mins` : "Unavailable"} • {edge.bucket || ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="dds2025__simple-sub">No drive edges cached yet.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button className="dds2025__nav-btn" onClick={() => setShowCalendarEventsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DragAndDropScheduler2025;
